import { getSupabase } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { callRpc } from '@/api/resources';

/**
 * Send a stored document to the people it concerns, by WhatsApp and / or email - never SMS - through
 * the send-consent-document edge function (owner, 2026-09-27: "need a provision in sending the
 * generated draft PDFs via whatsapp / email", "Post providing Consent, the completed docs also need
 * to be shared"). The function reads the contacts from each party's own record; the browser names
 * only the record, the document and the channels. Says in a toast what went and what did not.
 */
export async function sendRecordDocument(input: {
  consentId?: string;
  tradeId?: string;
  /** For a sale: the supplier, the buyer, or both (the default). */
  sides?: Array<'supplier' | 'buyer'>;
  documentId: string;
  /** Further copies sent with it: a consent's copy in the farmer's language beside the English (owner, 2026-09-28). */
  documentIds?: string[];
  channels: Array<'whatsapp' | 'email'>;
  /** Which of the four stages this is: the draft to read, or the final consented copy. */
  stage: 'draft' | 'final';
  /** With the final consent copy: the review link's token, sent by WhatsApp and email (never SMS). */
  reviewToken?: string | null;
}): Promise<boolean> {
  if (input.channels.length === 0) { toast.error('Tick WhatsApp or email. With neither, print the document.'); return false; }
  const { data, error } = await getSupabase().functions.invoke('send-consent-document', {
    body: { consent_id: input.consentId, trade_id: input.tradeId, sides: input.sides, document_id: input.documentId, document_ids: input.documentIds, channels: input.channels, stage: input.stage, review_token: input.reviewToken ?? undefined },
  });
  type Outcome = { results?: Array<{ side?: string; channel: string; destination_masked: string; delivered: boolean; error?: string }>; error?: string };
  // A non-2xx answer keeps its body on the error: a partial failure still lists what went.
  const payload: Outcome = error
    ? await ((error as { context?: Response }).context?.json?.() as Promise<Outcome> | undefined)?.catch(() => ({})) ?? {}
    : (data ?? {}) as Outcome;
  if (error && !payload.results) {
    const status = (error as { context?: Response }).context?.status;
    toast.error(payload.error ?? (status === 404 ? 'The sending service is not deployed yet: print the document for now.' : 'The document could not be sent.'));
    return false;
  }
  const label = (row: NonNullable<Outcome['results']>[number]) =>
    `${row.side ? `${row.side} ` : ''}${row.channel === 'email' ? 'email' : row.channel === 'sms' ? 'SMS notice' : 'WhatsApp'} (${row.destination_masked})`;
  const went = (payload.results ?? []).filter((row) => row.delivered);
  const failed = (payload.results ?? []).filter((row) => !row.delivered);
  if (went.length) toast.success(`Sent to ${went.map(label).join(', ')}.`);
  if (failed.length) toast.error(`Not sent to ${failed.map((row) => `${label(row)}: ${row.error}`).join('; ')}`);
  return went.length > 0;
}

/**
 * Stage 3 (owner, 2026-09-27: "validation success message to party (auto on OTP validation)"): tell
 * the party, at once and on every channel they have, that their consent or confirmation was
 * recorded. The server sends it only for a record that is in fact confirmed. Never throws: the
 * confirmation stands whether or not the message gets through, and the toast says which.
 */
export async function sendConfirmationNotice(input: { consentId?: string; tradeId?: string; side?: 'supplier' | 'buyer' }): Promise<void> {
  try {
    const { data, error } = await getSupabase().functions.invoke('send-consent-document', {
      body: { mode: 'notice', consent_id: input.consentId, trade_id: input.tradeId, sides: input.side ? [input.side] : undefined },
    });
    const payload = (error ? await ((error as { context?: Response }).context?.json?.() as Promise<{ results?: Array<{ channel: string; delivered: boolean }> }> | undefined)?.catch(() => ({})) ?? {} : data ?? {}) as
      { results?: Array<{ channel: string; delivered: boolean }>; error?: string };
    const went = (payload.results ?? []).filter((row) => row.delivered).map((row) => (row.channel === 'sms' ? 'SMS' : row.channel === 'email' ? 'email' : 'WhatsApp'));
    if (went.length) toast.success(`The party was told it is recorded, by ${went.join(', ')}.`);
    else toast.error('Recorded, but the confirmation message could not be sent to the party.');
  } catch {
    toast.error('Recorded, but the confirmation message could not be sent to the party.');
  }
}

/**
 * A private review link to a consent - the signed copy once it is given, else the draft - valid
 * 30 days (owner, 2026-09-27: "open a link post consent via whats app and email for review?"). The
 * token is 32 random bytes; only its hash is stored. Returns the token and the page URL.
 */
export async function issueConsentReviewLink(consentId: string, stage: 'draft' | 'signed'): Promise<{ token: string; url: string }> {
  const rows = await callRpc<Array<{ token: string }>>('fn_issue_consent_link', { p_consent_id: consentId, p_stage: stage, p_valid_hours: 720 });
  const token = rows?.[0]?.token;
  if (!token) throw new Error('The review link could not be made.');
  return { token, url: `${window.location.origin}/consent/${token}` };
}
